import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  formatMethodConfig,
  parseMethodConfig,
  PAY_METHOD_LABELS,
  type AdminSetting,
  type MethodConfigRow,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Textarea,
} from "@alight/ui";
import {
  ArrowDown,
  ArrowUp,
  Eye,
  EyeOff,
  Landmark,
  MailOpen,
  RotateCcw,
  Timer,
  Users,
} from "lucide-react";

/** Vite proxies `/api` to the Worker in dev; relative base is correct. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * Settings — host-editable operational copy, bank details and timing.
 *
 * Every field shows its EFFECTIVE value (saved override, or the deploy
 * default). Saving writes an override row; "Reset to default" deletes it, so
 * the wrangler/env default takes over again. Notice bodies support
 * {{placeholders}} — each field's description documents which.
 */

interface SectionDef {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  match: (s: AdminSetting) => boolean;
}

const SECTIONS: SectionDef[] = [
  {
    title: "Bank details",
    description:
      "The account parents transfer the enrolment fee to, and who receives payments in person. Shown on the enrol/pay pages and in payment emails.",
    icon: Landmark,
    match: (s) => s.group === "bank",
  },
  {
    title: "Notices to families",
    description:
      "The wording of the emails parents receive. Placeholders like {{parent_name}} are filled in automatically — each field lists the ones it supports.",
    icon: MailOpen,
    match: (s) => s.group === "notices" && s.key.startsWith("notices.parent_"),
  },
  {
    title: "Notices to the team",
    description: "The wording of the account manager and admin summary emails.",
    icon: Users,
    match: (s) => s.group === "notices" && !s.key.startsWith("notices.parent_"),
  },
  {
    title: "Timing",
    description: "Business-day windows for payment clearing and reconcile reminders.",
    icon: Timer,
    match: (s) => s.group === "timing",
  },
];

export function AdminSettings() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => api.adminListSettings(),
  });

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string | null }) =>
      api.adminSaveSetting({ key, value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "settings"] }),
  });

  const settings = settingsQuery.data ?? [];

  return (
    <section className="space-y-8">
      <header>
        <h2 className="font-heading text-xl font-bold text-ink">Settings</h2>
        <p className="mt-1 text-sm text-muted">
          Bank details, email wording and timing — editable here, no code required. Fields
          marked <Badge variant="accent">Customised</Badge> override the deploy default;
          reset one to go back to the default.
        </p>
      </header>

      {settingsQuery.isLoading ? (
        <p className="text-sm text-muted">Loading settings…</p>
      ) : settingsQuery.isError ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-ink">
              Couldn’t load settings.{" "}
              {settingsQuery.error instanceof ApiError
                ? settingsQuery.error.message
                : "Please retry."}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => settingsQuery.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        SECTIONS.map((section) => {
          const items = settings.filter(section.match);
          if (items.length === 0) return null;
          const Icon = section.icon;
          return (
            <div key={section.title} className="space-y-3">
              <div className="flex items-start gap-2">
                <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
                <div>
                  <h3 className="font-heading text-base font-bold text-ink">
                    {section.title}
                  </h3>
                  <p className="text-sm text-muted">{section.description}</p>
                </div>
              </div>
              <Card>
                <CardContent className="divide-y divide-border p-0">
                  {items.map((setting) => (
                    <SettingField
                      // Value/custom in the key remounts the row with fresh
                      // local state after every save or reset.
                      key={`${setting.key}:${setting.custom}:${setting.value}`}
                      setting={setting}
                      pending={save.isPending && save.variables?.key === setting.key}
                      error={
                        save.isError && save.variables?.key === setting.key
                          ? save.error instanceof ApiError
                            ? String(
                                (save.error.body as { error?: string } | undefined)?.error ??
                                  save.error.message,
                              )
                            : "Couldn’t save — please retry."
                          : null
                      }
                      onSave={(value) => save.mutate({ key: setting.key, value })}
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
          );
        })
      )}
    </section>
  );
}

/**
 * Reorder + show/hide the payment options a parent sees.
 *
 * Up/down buttons rather than HTML5 drag on purpose: the same idiom the form
 * builder already uses (admin/forms.tsx), it works on touch, and it's operable
 * from the keyboard — a drag-only control would lock out anyone not using a
 * mouse. Reordering is announced so screen-reader users hear the new position.
 */
function MethodOrderEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const rows = parseMethodConfig(value);
  const [announcement, setAnnouncement] = React.useState("");

  function commit(next: MethodConfigRow[]) {
    onChange(formatMethodConfig(next));
  }

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(target, 0, moved);
    commit(next);
    setAnnouncement(`${PAY_METHOD_LABELS[moved.method]} moved to position ${target + 1}`);
  }

  function toggle(index: number) {
    const next = rows.map((r, i) => (i === index ? { ...r, visible: !r.visible } : r));
    const changed = next[index];
    if (!changed) return;
    commit(next);
    setAnnouncement(
      `${PAY_METHOD_LABELS[changed.method]} ${changed.visible ? "shown to" : "hidden from"} parents`,
    );
  }

  const visibleCount = rows.filter((r) => r.visible).length;

  return (
    <div className="space-y-2">
      <ol className="divide-y divide-border rounded-card border border-border">
        {rows.map((row, i) => (
          <li key={row.method} className="flex items-center gap-3 px-3 py-2">
            <span className="w-5 text-xs tabular-nums text-muted">{i + 1}</span>
            <span
              className={
                row.visible ? "flex-1 text-sm text-ink" : "flex-1 text-sm text-muted line-through"
              }
            >
              {PAY_METHOD_LABELS[row.method]}
            </span>
            {!row.visible && <Badge variant="muted">Hidden</Badge>}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={i === 0}
              aria-label={`Move ${PAY_METHOD_LABELS[row.method]} up`}
              onClick={() => move(i, -1)}
            >
              <ArrowUp className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={i === rows.length - 1}
              aria-label={`Move ${PAY_METHOD_LABELS[row.method]} down`}
              onClick={() => move(i, 1)}
            >
              <ArrowDown className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              /* Never let the host leave parents with no way to pay. */
              disabled={row.visible && visibleCount === 1}
              aria-label={`${row.visible ? "Hide" : "Show"} ${PAY_METHOD_LABELS[row.method]}`}
              onClick={() => toggle(i)}
            >
              {row.visible ? (
                <Eye className="h-4 w-4" aria-hidden />
              ) : (
                <EyeOff className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </li>
        ))}
      </ol>
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
      {visibleCount === 1 && (
        <p className="text-xs text-muted">
          This is the only option parents can pick — it can't be hidden.
        </p>
      )}
    </div>
  );
}

function SettingField({
  setting,
  pending,
  error,
  onSave,
}: {
  setting: AdminSetting;
  pending: boolean;
  error: string | null;
  onSave: (value: string | null) => void;
}) {
  const [value, setValue] = React.useState(setting.value);
  const dirty = value !== setting.value;
  const inputId = `setting-${setting.key.replace(/\./g, "-")}`;

  return (
    <div className="space-y-2 px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor={inputId} className="font-semibold text-ink">
          {setting.label}
        </Label>
        {setting.custom && <Badge variant="accent">Customised</Badge>}
      </div>
      <p className="text-xs text-muted">{setting.description}</p>

      {setting.kind === "order" ? (
        <MethodOrderEditor value={value} onChange={setValue} />
      ) : setting.kind === "multiline" ? (
        <Textarea
          id={inputId}
          value={value}
          rows={Math.min(10, Math.max(3, value.split("\n").length + 1))}
          onChange={(e) => setValue(e.target.value)}
          className="font-mono text-sm"
        />
      ) : (
        <Input
          id={inputId}
          type={setting.kind === "number" ? "number" : "text"}
          min={setting.kind === "number" ? 1 : undefined}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="max-w-sm"
        />
      )}

      {error && (
        <p className="text-sm text-accent-fg" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!dirty || pending} onClick={() => onSave(value)}>
          {pending ? "Saving…" : "Save"}
        </Button>
        {setting.custom && (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => onSave(null)}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Reset to default
          </Button>
        )}
      </div>
    </div>
  );
}
